import { pathValue as $value, atom as $atom } from '@graphistry/falcor-json-graph';
import { Observable } from '@reactivex/rxjs';
import {
    TOGGLE_OPEN,
    CLOSE,
    SET_INPUT,
    SEND_MESSAGE,
    RECEIVE_START,
    RECEIVE_CHUNK,
    RECEIVE_THINKING,
    PROMOTE_THINKING,
    RECEIVE_DONE,
    RECEIVE_ERROR,
    RECEIVE_RUN_UPDATE,
    RECEIVE_ELEMENT,
    CLEAR_CONTEXT,
    RECEIVE_CONTEXT,
    RECEIVE_STARTERS,
    RECEIVE_SKILL,
    STOP,
    SET_STREAMING_MODE,
} from '@graphistry/viz/src/actions/chat';

const STATE_MUTATION_TYPES = new Set([
    SEND_MESSAGE,
    RECEIVE_START,
    RECEIVE_CHUNK,
    RECEIVE_THINKING,
    PROMOTE_THINKING,
    RECEIVE_DONE,
    RECEIVE_ERROR,
    RECEIVE_RUN_UPDATE,
    RECEIVE_ELEMENT,
    CLEAR_CONTEXT,
]);

function elementDedupKey(el) {
    const meta = el && el.metadata;
    if (!meta || !meta.shape || !meta.columns) return null;
    const cols = Object.keys(meta.columns).sort().join(',');
    if (!cols) return null;
    return [el.type, JSON.stringify(meta.shape), cols].join('|');
}

function getChatModel(falcor) {
    return falcor._clone({
        _path: [...falcor.getPath().slice(0, 4), 'chat']
    });
}

async function readChat(chatModel) {
    const res = await chatModel.get(
        'isOpen', 'inputText', 'isLoading', 'currentResponseId', 'runStartTime',
        'messages', 'runNodes', 'elements'
    ).toPromise();
    const chatState = res?.json || {};
    return {
        isOpen: !!chatState.isOpen,
        inputText: chatState.inputText || '',
        isLoading: !!chatState.isLoading,
        currentResponseId: chatState.currentResponseId || 0,
        runStartTime: chatState.runStartTime || 0,
        messages: Array.isArray(chatState.messages) ? chatState.messages : [],
        runNodes: (chatState.runNodes && typeof chatState.runNodes === 'object') ? chatState.runNodes : {},
        elements: (chatState.elements && typeof chatState.elements === 'object') ? chatState.elements : {},
    };
}

function applyAction(cur, action) {
    switch (action.type) {
        case SEND_MESSAGE: {
            return {
                messages: [...cur.messages, { id: Date.now(), role: 'user', content: action.text }],
                inputText: '',
                isLoading: true,
            };
        }
        case RECEIVE_START: {
            const now = Date.now();
            return {
                currentResponseId: now,
                runNodes: {},
                elements: {},
                runStartTime: now,
            };
        }
        case RECEIVE_CHUNK: {
            const msgs = [...cur.messages];
            let assistantIdx = -1;
            for (let i = msgs.length - 1; i >= 0; i--) {
                if (msgs[i].role === 'assistant' && msgs[i].responseId === cur.currentResponseId) {
                    assistantIdx = i;
                    break;
                }
            }
            if (assistantIdx >= 0) {
                msgs[assistantIdx] = { ...msgs[assistantIdx], content: msgs[assistantIdx].content + action.content };
            } else {
                msgs.push({ id: Date.now(), role: 'assistant', content: action.content, responseId: cur.currentResponseId });
            }
            return { messages: msgs };
        }
        case RECEIVE_THINKING: {
            const msgs = [...cur.messages];
            const last = msgs[msgs.length - 1];
            if (last && last.role === 'thinking' && last.responseId === cur.currentResponseId) {
                msgs[msgs.length - 1] = { ...last, content: last.content + action.content };
            } else {
                msgs.push({ id: Date.now(), role: 'thinking', content: action.content, responseId: cur.currentResponseId });
            }
            return { messages: msgs };
        }
        case PROMOTE_THINKING: {
            const msgs = [...cur.messages];
            const last = msgs[msgs.length - 1];
            if (last && last.role === 'assistant') {
                msgs[msgs.length - 1] = { ...last, role: 'thinking' };
            }
            msgs.push({ id: Date.now(), role: 'assistant', content: '', responseId: cur.currentResponseId });
            return { messages: msgs };
        }
        case RECEIVE_DONE: {
            const doneMessages = [...cur.messages];
            const elementList = Object.values(cur.elements || {});
            let attached = false;
            if (Object.keys(cur.runNodes).length > 0 || elementList.length > 0) {
                for (let i = doneMessages.length - 1; i >= 0; i--) {
                    if (doneMessages[i].role === 'assistant' && doneMessages[i].responseId === cur.currentResponseId) {
                        doneMessages[i] = { ...doneMessages[i], runNodes: cur.runNodes, elements: elementList, runStartTime: cur.runStartTime, runDuration: Date.now() - cur.runStartTime };
                        attached = true;
                        break;
                    }
                }
            }
            if (!attached && elementList.length > 0) {
                doneMessages.push({ id: Date.now(), role: 'assistant', content: '', responseId: cur.currentResponseId, elements: elementList });
            }
            return {
                messages: doneMessages,
                isLoading: false,
                runNodes: {},
                elements: {},
            };
        }
        case RECEIVE_ERROR: {
            return {
                messages: [...cur.messages, { id: Date.now(), role: 'error', content: action.content || 'Something went wrong.' }],
                isLoading: false,
            };
        }
        case RECEIVE_RUN_UPDATE: {
            return {
                runNodes: { ...cur.runNodes, [action.node.id]: action.node },
            };
        }
        case RECEIVE_ELEMENT: {
            if (!action.element || !action.element.id) return null;
            // The agent loop re-runs near-identical queries and emits an element for each
            // (graphistrygpt#3808). Same shape + same columns within one turn is a repeat;
            // keep the newest, since a refined run is likelier to be the answer.
            const key = elementDedupKey(action.element);
            const elements = { ...cur.elements };
            if (key) {
                for (const id of Object.keys(elements)) {
                    if (elementDedupKey(elements[id]) === key) delete elements[id];
                }
            }
            elements[action.element.id] = action.element;
            return { elements };
        }
        case CLEAR_CONTEXT: {
            return {
                messages: [],
                inputText: '',
                isLoading: false,
                runNodes: {},
                elements: {},
            };
        }
        default:
            return null;
    }
}

function buildSetArgs(patch) {
    const setters = [];
    if ('messages' in patch) setters.push($value('messages', $atom(patch.messages)));
    if ('runNodes' in patch) setters.push($value('runNodes', $atom(patch.runNodes)));
    if ('elements' in patch) setters.push($value('elements', $atom(patch.elements)));
    if ('inputText' in patch) setters.push($value('inputText', patch.inputText));
    if ('isLoading' in patch) setters.push($value('isLoading', patch.isLoading));
    if ('currentResponseId' in patch) setters.push($value('currentResponseId', patch.currentResponseId));
    if ('runStartTime' in patch) setters.push($value('runStartTime', patch.runStartTime));
    return setters;
}

export function chat(action$, store) {
    return Observable.merge(
        updateChatState(action$),
        toggleChatOpen(action$),
        closeChatPanel(action$),
        updateInputText(action$),
        stopChatResponse(action$),
        receiveChatContext(action$),
        receiveChatStarters(action$),
        receiveActiveSkill(action$),
        setStreamingMode(action$),
    ).ignoreElements();
}

function updateChatState(action$) {
    return action$
        .filter(a => STATE_MUTATION_TYPES.has(a.type))
        .concatMap(action => {
            const chatModel = getChatModel(action.falcor);
            return Observable.fromPromise(
                readChat(chatModel).then(cur => {
                    const patch = applyAction(cur, action);
                    if (!patch) return null;
                    const setters = buildSetArgs(patch);
                    if (setters.length === 0) return null;
                    return chatModel.set(...setters).toPromise();
                })
            );
        });
}

function toggleChatOpen(action$) {
    return action$.ofType(TOGGLE_OPEN).concatMap(({ falcor }) => {
        const chatModel = getChatModel(falcor);
        return Observable.fromPromise(
            chatModel.get('isOpen').toPromise().then(res => {
                const cur = !!res?.json?.isOpen;
                return chatModel.set($value('isOpen', !cur)).toPromise();
            })
        );
    });
}

function closeChatPanel(action$) {
    return action$.ofType(CLOSE).switchMap(({ falcor }) => {
        return getChatModel(falcor).set($value('isOpen', false));
    });
}

function updateInputText(action$) {
    return action$.ofType(SET_INPUT).switchMap(({ falcor, text }) => {
        return getChatModel(falcor).set($value('inputText', text));
    });
}

function stopChatResponse(action$) {
    return action$.ofType(STOP).switchMap(({ falcor }) => {
        return getChatModel(falcor).set($value('isLoading', false));
    });
}

function receiveChatContext(action$) {
    return action$.ofType(RECEIVE_CONTEXT).switchMap(({ falcor, context }) => {
        return getChatModel(falcor).set($value('context', $atom(context || null)));
    });
}

function receiveChatStarters(action$) {
    return action$.ofType(RECEIVE_STARTERS).switchMap(({ falcor, starters, startersError }) => {
        return getChatModel(falcor).set(
            $value('starters', $atom(starters || null)),
            $value('startersError', !!startersError)
        );
    });
}

function receiveActiveSkill(action$) {
    return action$.ofType(RECEIVE_SKILL).switchMap(({ falcor, name }) => {
        return getChatModel(falcor).set($value('activeSkill', name || null));
    });
}

function setStreamingMode(action$) {
    return action$.ofType(SET_STREAMING_MODE).switchMap(({ falcor, value }) => {
        return getChatModel(falcor).set($value('streamingMode', value));
    });
}
